import { FetchUnauthorizedRequest } from "@jnctn/error-handling/Errors";
import handleNotOkResponse from "@jnctn/error-handling/handleNotOkResponse";

export type Notification = {
  notificationId: string;
  coreUserId: string;
  notificationTypeId: string;
  notificationTypeName: string;
  notificationDate: string;
  message: string;
  targetId: string;
  unread: boolean;
};

export type Result = {
  pages: Array<Notification>;
  totalRows: number;
};

export function getTotal(result: Result): number {
  return result.totalRows;
}

export async function fetchNotificationSummary(
  accessToken: string | undefined,
  tenantId: string
): Promise<{ unreadRows: number; totalRows: number }> {
  if (accessToken === undefined) {
    throw new FetchUnauthorizedRequest(
      fetchNotificationSummary,
      "Unauthorized"
    );
  }

  const res = await fetch(
    `${process.env.REACT_APP_QUERY_API_URL}/app/notification/summary/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "JNCTN-TENANT-ID": tenantId,
      },
      credentials: "include",
    }
  );

  if (!res.ok) {
    handleNotOkResponse(res, fetchNotificationSummary);
  }

  return await res.json();
}

export async function fetchNotificationUnreadCount(
  accessToken: string | undefined,
  tenantId: string
): Promise<number> {
  if (accessToken === undefined) {
    throw new FetchUnauthorizedRequest(
      fetchNotificationUnreadCount,
      "Unauthorized"
    );
  }

  const res = await fetch(
    `${process.env.REACT_APP_QUERY_API_URL}/app/notification/summary/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "JNCTN-TENANT-ID": tenantId,
      },
      credentials: "include",
    }
  );

  if (!res.ok) {
    handleNotOkResponse(res, fetchNotificationUnreadCount);
  }

  return (await res.json()).unreadRows;
}

export async function fetchMarkNotificationRead(
  accessToken: string | undefined,
  tenantId: string,
  notificationId: string
): Promise<string> {
  if (accessToken === undefined) {
    throw new FetchUnauthorizedRequest(
      fetchMarkNotificationRead,
      "Unauthorized"
    );
  }

  let res: Response | undefined;
  try {
    res = await fetch(
      `${process.env.REACT_APP_QUERY_API_URL}/app/notification/`,
      {
        method: "PUT",
        credentials: "include",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "JNCTN-TENANT-ID": tenantId,
        },
        body: JSON.stringify({ notificationId, unread: false }),
      }
    );
  } catch (e) {
    throw new FetchUnauthorizedRequest(
      fetchMarkNotificationRead,
      "Unauthorized"
    );
  }

  if (!res.ok) {
    handleNotOkResponse(res, fetchMarkNotificationRead);
  }

  return (await res.json()).notificationId;
}

export async function fetchMarkNotificationsRead(
  accessToken: string | undefined,
  tenantId: string,
  ids: Array<string>
): Promise<Array<string>> {
  return Promise.all(
    ids.map((id) => fetchMarkNotificationRead(accessToken, tenantId, id))
  );
}

export async function fetchNotifications(
  skip: number,
  top: number,
  accessToken: string | undefined,
  tenantId: string,
  unreadOnly: boolean
): Promise<Result> {
  if (accessToken === undefined) {
    return { pages: [], totalRows: 0 };
  }

  let res: Response | undefined;
  try {
    res = await fetch(
      `${process.env.REACT_APP_QUERY_API_URL}/app/notification/filter`,
      {
        method: "POST",
        credentials: "include",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "JNCTN-TENANT-ID": tenantId,
        },
        body: JSON.stringify({
          skip,
          top,
          filter: {
            unreadOnly,
          },
          orders: [
            {
              column: "NotificationDate",
              direction: "Asc",
              rank: 0,
            },
          ],
        }),
      }
    );
  } catch (e) {
    throw new FetchUnauthorizedRequest(fetchNotifications, "Unauthorized");
  }

  if (!res.ok) {
    handleNotOkResponse(res, fetchNotifications);
  }

  return await res.json();
}
